// Powiadomienia i alarmy konta (docs/powiadomienia/PROJEKT.md): ustawienia, telefony z pushem, próbne, lista
// alarmów. Wysyła worker - APP tylko zapisuje ustawienia i subskrypcję tego telefonu.
import { useSyncExternalStore } from "react";
import { t } from "../i18n/t";
import { pushWAplikacji, wtyczkaPowiadomien, type DzwiekAlarmu } from "../uklad/wtyczkaPowiadomien";
import { androidBezPwa } from "../uklad/instalacja";
import type {
  AlarmMuteIn, AlarmReadReply, AlarmsReply, AlarmUnreadIn, NotifyReply, NotifyTestIn, NotifyTestReply, OKReply, OkresAlarmow, Prefs, StanAlarmow, Waznosc,
} from "../gen/kontrakt";
import { zapytanie } from "./zaplecze";

export const powiadomienia = () => zapytanie<NotifyReply>("GET", "/api/powiadomienia");

export const zapiszPowiadomienia = (p: Prefs) => zapytanie<OKReply>("PUT", "/api/powiadomienia", p);

/** Klucz wyciszenia alarmu urządzenia w ustawieniach (`alarmy`). */
export const kluczAlarmu = (sztuka: number, alarm: string) => sztuka + ":" + alarm;

/** Wyciszenie rodzaju alarmu na jego urządzeniu (z listy alarmów; sw.js i aplikacja - przycisk w powiadomieniu):
 *  cicho / wyłączone, "" = przywróć. Serwer zapisuje w ustawieniach - nie nadpisuje zmian z innego telefonu. */
export const wyciszAlarm = (id: number, tryb: AlarmMuteIn["tryb"]) =>
  zapytanie<OKReply>("POST", `/api/alarmy/${id}/wycisz`, { tryb } satisfies AlarmMuteIn);

/** Próbne (WYGLAD.md #59): `telefon` z listy = push na ten telefon, 0 = e-mail. */
export const proba = (telefon: number) => zapytanie<NotifyTestReply>("POST", "/api/powiadomienia/proba", { telefon } satisfies NotifyTestIn);

export const usunTelefon = (id: number, endpoint = "") =>
  zapytanie<OKReply>("DELETE", `/api/powiadomienia/telefony/${id}` + (endpoint ? "?endpoint=" + encodeURIComponent(endpoint) : ""));

// Aplikacja ze sklepu na Androidzie (docs/pwa/NATYWNA.md 4, krok 3): push przez FCM - wtyczka natywna `Powiadomienia`
// (uklad/wtyczkaPowiadomien.ts) daje token, na koncie zapisany jako `{fcm}`; powiadomienia składa aplikacja (kanał na
// rodzaj - nazwy z APP). W przeglądarce - Web Push jak dotąd.
const W = wtyczkaPowiadomien;
const wAplikacji = pushWAplikacji;

// Nazwy kanałów Androida = rodzaje z Ustawień (te same teksty co ekrany/ustawienia/Powiadomienia.tsx KAT).
const kanaly = () => ({ k_alarmy_krytyczne: t("Alarmy krytyczne"), k_ostrzezenia: t("Ostrzeżenia"),
  k_postep: t("Postęp procesu"), k_udostepnienia: t("Udostępnienia"), k_serwis: t("Serwis"), k_konto: t("Konto i licencja"),
  ciche: t("Ciche"), na_zywo: t("Karta na żywo") });

const zapiszToken = (token: string) => zapytanie<OKReply>("POST", "/api/powiadomienia/telefony", { fcm: token });

/** `w_aplikacji` - przeglądarka na Androidzie: push tylko przez aplikację TermometrWifi (właściciel 29.10 - z PWA
 *  i aplikacją na jednym telefonie każde powiadomienie przychodziło dwa razy). */
export type StanPushu = "brak" | "zablokowany" | "wylaczony" | "wlaczony" | "w_aplikacji";

/** Web Push przeglądarki precz (konto i przeglądarka). */
async function wylaczWebPush(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  const rej = await navigator.serviceWorker.ready;
  const sub = await rej.pushManager.getSubscription();
  if (!sub) return;
  await usunTelefon(0, sub.endpoint).catch(() => undefined);
  await sub.unsubscribe();
}

/** Push na tym telefonie: w aplikacji `brak` - starsza wersja bez pushu; w przeglądarce - bez Web Push; przeglądarka
 *  na Androidzie - `w_aplikacji` (subskrypcja sprzed 29.10 od razu wyłączona). */
export async function stanPushu(): Promise<StanPushu> {
  if (wAplikacji()) {
    const s = await W.stan().catch(() => null);
    if (!s?.dostepne) return "brak";
    return s.wylaczone ? "wylaczony" : s.zgoda ? "wlaczony" : "zablokowany";
  }
  if (androidBezPwa()) {
    await wylaczWebPush();
    return "w_aplikacji";
  }
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return "brak";
  if (Notification.permission === "denied") return "zablokowany";
  const rej = await navigator.serviceWorker.ready;
  return (await rej.pushManager.getSubscription()) ? "wlaczony" : "wylaczony";
}

/** Push na tym telefonie: zgoda, subskrypcja (aplikacja - token FCM), zapis na koncie. Zwraca false bez zgody. */
export async function wlaczPush(klucz: string): Promise<boolean> {
  if (wAplikacji()) {
    const r = await W.token({ pytaj: true, kanaly: kanaly() }).catch(() => null);
    if (!r) return false;
    await zapiszToken(r.token);
    await W.bateria({}).catch(() => undefined); // starsza aplikacja bez prośby o baterię
    return true;
  }
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || androidBezPwa()) return false;
  if ((await Notification.requestPermission()) !== "granted") return false;
  const rej = await navigator.serviceWorker.ready;
  const sub = (await rej.pushManager.getSubscription()) ??
    (await rej.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: kluczSerwera(klucz) }));
  await zapytanie<OKReply>("POST", "/api/powiadomienia/telefony", sub.toJSON());
  return true;
}

/** Wyłączenie pushu na tym telefonie (konto i przeglądarka / aplikacja). */
export async function wylaczPush(): Promise<void> {
  if (wAplikacji()) {
    const r = await W.token({ kanaly: kanaly() }).catch(() => null);
    if (r) await usunTelefon(0, r.token).catch(() => undefined);
    await W.usun();
    return;
  }
  await wylaczWebPush();
}

/** Po starcie APP: subskrypcja tego telefonu odświeżona na koncie (usługa push mogła ją zmienić). Aplikacja - token
 *  przy zgodzie na powiadomienia (o nią pyta pierwszy start, uklad/wykrywanie.ts), chyba że wyłączony ręcznie, i jeden
 *  raz na telefon prośba o działanie w tle bez ograniczeń baterii (dalej - przycisk w Ustawieniach).
 *  Przeglądarka na Androidzie - subskrypcja sprzed 29.10 precz (push tylko przez aplikację). */
export async function odswiezPush(): Promise<void> {
  if (wAplikacji()) {
    const r = await W.token({ kanaly: kanaly() }).catch(() => null);
    if (!r) return;
    await zapiszToken(r.token).catch(() => undefined);
    await W.bateria({ raz: true }).catch(() => undefined);
    return;
  }
  if (androidBezPwa()) {
    await wylaczWebPush().catch(() => undefined);
    return;
  }
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || Notification.permission !== "granted") return;
  const rej = await navigator.serviceWorker.ready;
  const sub = await rej.pushManager.getSubscription();
  if (sub) await zapytanie<OKReply>("POST", "/api/powiadomienia/telefony", sub.toJSON()).catch(() => undefined);
}

/** Aplikacja: ustawienia kanału alarmów krytycznych w telefonie („Ignoruj Nie przeszkadzać” włącza człowiek). */
export const ustawieniaAlarmowTelefonu = () => W.ustawieniaAlarmow();

/** Stare APK (27-35) podają tylko `dzwiek` - mają te cztery. */
const DZWIEKI_STARE: DzwiekAlarmu[] = ["budzik", "syrena", "pikanie", "dzwon"];

/** Aplikacja: wybrany dźwięk alarmów krytycznych, dźwięki tej aplikacji i nazwa wybranego z telefonu; null - aplikacja
 *  bez wyboru (starszy APK) albo przeglądarka. */
export const dzwiekAlarmu = (): Promise<{ dzwiek: DzwiekAlarmu; dostepne: DzwiekAlarmu[]; nazwa: string } | null> =>
  wAplikacji() ? W.stan().then((s) => s.dzwiek === undefined ? null
    : { dzwiek: s.dzwiek, dostepne: s.dzwieki ?? DZWIEKI_STARE, nazwa: s.dzwiekNazwa ?? "" }, () => null)
    : Promise.resolve(null);

/** Zapis dźwięku - telefon od razu go gra (3 s); `telefon` - najpierw systemowe okno wyboru, wynik: nazwa wybranego
 *  (rezygnacja - odmowa `anulowano`). */
export const ustawDzwiekAlarmu = (dzwiek: DzwiekAlarmu) => W.dzwiek({ dzwiek }).then((w) => w?.nazwa ?? "");

/** Aplikacja: alarm krytyczny przebija „Nie przeszkadzać” (Cisza.java) - opcja i dostęp dany w telefonie; null -
 *  aplikacja bez tej opcji (starszy APK) albo przeglądarka. */
export const przebijanieCiszy = (): Promise<{ wlaczone: boolean; dostep: boolean } | null> =>
  wAplikacji() ? W.stan().then((s) => s.przebijCisze === undefined ? null : { wlaczone: s.przebijCisze, dostep: s.dostepCiszy === true },
    () => null) : Promise.resolve(null);

/** Włączenie bez dostępu otwiera ekran telefonu z dostępem do „Nie przeszkadzać”. */
export const ustawPrzebijanieCiszy = (wlacz: boolean) => W.przebijCisze({ wlacz });

/** Aplikacja: działa w tle bez ograniczeń baterii; null - aplikacja bez tej prośby (starszy APK) albo przeglądarka. */
export const bateriaBezOgraniczen = (): Promise<boolean | null> =>
  wAplikacji() ? W.stan().then((s) => s.bateria ?? null, () => null) : Promise.resolve(null);

/** Prośba telefonu o działanie w tle bez ograniczeń (stan sprawdza APP po powrocie). */
export const zezwolBaterii = () => W.bateria({}).then(() => undefined);

/** Dotknięte powiadomienie w aplikacji → `idz(ścieżka)` (adres z treści pushu jest pełny). Zwraca wyłączenie. */
export function naOtwartePowiadomienie(idz: (sciezka: string) => void): () => void {
  if (!wAplikacji()) return () => {};
  const n = W.addListener("otwarte", (e) => {
    const u = new URL(e.url, location.origin);
    idz(u.pathname + u.search + u.hash);
  }).catch(() => null); // starsza aplikacja bez wtyczki
  return () => void n.then((h) => h?.remove());
}

function kluczSerwera(b64: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (b64.length % 4)) % 4));
  const out = new Uint8Array(new ArrayBuffer(s.length));
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

/** Filtry listy alarmów (liczy serwer); "" / 0 = każdy. */
export interface FiltrAlarmow {
  stan: StanAlarmow | "";
  waznosc: Waznosc | "";
  sn: string;
  okres: OkresAlarmow | "";
  alarm: string;
}

export const BEZ_FILTRA: FiltrAlarmow = { stan: "", waznosc: "", sn: "", okres: "", alarm: "" };

export function alarmy(f: FiltrAlarmow, strona: number): Promise<AlarmsReply> {
  const q = new URLSearchParams({ strona: String(strona) });
  for (const [k, v] of Object.entries(f)) if (v) q.set(k, v);
  return zapytanie<AlarmsReply>("GET", "/api/alarmy?" + q.toString());
}

/** „Przeczytane” jednego alarmu albo (id 0) wszystkich nieprzeczytanych; wynik - oznaczone teraz (do „Cofnij”). */
export const przeczytane = (id: number) =>
  zapytanie<AlarmReadReply>("POST", id ? `/api/alarmy/${id}/przeczytany` : "/api/alarmy/przeczytane")
    .then((r) => r.alarmy).finally(() => void odswiezNoweAlarmy());

/** „Cofnij” w toście po „Przeczytane” (WYGLAD.md #6). */
export const cofnijPrzeczytane = (ids: number[]) =>
  zapytanie<OKReply>("POST", "/api/alarmy/nieprzeczytane", { alarmy: ids } satisfies AlarmUnreadIn).finally(() => void odswiezNoweAlarmy());

// Liczba nieprzeczytanych alarmów konta na dzwonku powłoki (WYGLAD.md #2) - licznik `nieprzeczytane` tej samej listy
// (serwer liczy ze wszystkich alarmów konta); odświeżany przy starcie, zdarzeniu `alarm.changed` i po „Przeczytane”.
let nowe = 0;
const sluchaczeNowych = new Set<() => void>();

export async function odswiezNoweAlarmy(): Promise<void> {
  const d = await alarmy({ ...BEZ_FILTRA, stan: "unread" }, 0).catch(() => null);
  if (!d || d.liczniki.nieprzeczytane === nowe) return;
  nowe = d.liczniki.nieprzeczytane;
  for (const f of sluchaczeNowych) f();
}

export function useNoweAlarmy(): number {
  return useSyncExternalStore((f) => { sluchaczeNowych.add(f); return () => sluchaczeNowych.delete(f); }, () => nowe);
}

export function wyczyscNoweAlarmy(): void {
  nowe = 0;
  for (const f of sluchaczeNowych) f();
}
